// "Pakete gösterge ekle" penceresi saf yardımcıları: Seçim paneli gruplama, otomatik eklenecek
// sayısı (sunucu kapanışıyla aynı kural) ve "Pakettekileri gizle" süzgeci.

import { parseFormula } from "@mahir/shared";
import {
  familyBelow,
  isEligibleItem,
  type DraftContext,
  type DraftMetric,
  type PackagePick,
} from "./kpi-package-draft";

export interface SelectionGroup {
  categoryId: string | null;
  label: string;
  items: PackagePick[];
}

const isGroupNode = (m: DraftMetric | undefined): boolean => m?.nodeKind === "breakdown_group";
const isMemberNode = (m: DraftMetric | undefined): boolean =>
  m?.dimensionMember !== null && m?.dimensionMember !== undefined;

function nearestCategory(id: string, ctx: DraftContext): DraftMetric | null {
  const seen = new Set<string>();
  let cur = ctx.byId.get(id);
  while (cur?.parentId && !seen.has(cur.id)) {
    seen.add(cur.id);
    const parent = ctx.byId.get(cur.parentId);
    if (!parent) return null;
    if (ctx.categories.has(parent.id)) return parent;
    cur = parent;
  }
  return null;
}

/** Seçimi en yakın kategoriye gruplar (ilk görülme sırası; kategorisizler "Kategorisiz"). */
export function selectionGroups(
  picks: readonly PackagePick[],
  ctx: DraftContext,
  noCategoryLabel = "",
): SelectionGroup[] {
  const groups = new Map<string | null, SelectionGroup>();
  for (const pick of picks) {
    const cat = nearestCategory(pick.metricId, ctx);
    const key = cat?.id ?? null;
    let g = groups.get(key);
    if (!g) {
      g = { categoryId: key, label: cat?.label ?? noCategoryLabel, items: [] };
      groups.set(key, g);
    }
    g.items.push(pick);
  }
  return [...groups.values()];
}

/** Kategoriye çözülen formül argümanı → altındaki eklenebilir toplam/yapraklar (R3-1). */
function categoryLeaves(id: string, ctx: DraftContext): string[] {
  const out: string[] = [];
  const walk = (cur: string): void => {
    for (const k of ctx.kids.get(cur) ?? []) {
      if (isGroupNode(k) || isMemberNode(k)) continue;
      if (ctx.categories.has(k.id)) walk(k.id);
      else out.push(k.id);
    }
  };
  walk(id);
  return out;
}

/**
 * Seçimin kaydedilince sunucuda otomatik ekleyeceği ek gösterge sayısı: seçilen türetilmişlerin
 * formül girdileri (yalnız kendileri; özyinelemeli türetilmişlerin girdileri de) + seçilen toplamların
 * kırılım aileleri (iki seviyede grup + uygun üyeler). Pakette/taslakta ya da seçimde olanlar sayılmaz.
 */
export function autoAddCount(
  picks: readonly PackagePick[],
  members: ReadonlySet<string>,
  ctx: DraftContext,
): number {
  const picked = new Set(picks.map((p) => p.metricId));
  const extra = new Set<string>();
  const have = (id: string): boolean => members.has(id) || picked.has(id);

  const addDeps = (id: string, depth: number, seen: Set<string>): void => {
    const m = ctx.byId.get(id);
    if (!m || depth > 10 || seen.has(id)) return;
    seen.add(id);
    for (const f of m.defaultFormulas) {
      const p = parseFormula(f.expression);
      if (!p.ok) continue;
      for (const code of p.refs) {
        const dep = ctx.byCode.get(code);
        if (!dep || dep.id === id) continue;
        const targets = ctx.categories.has(dep.id) ? categoryLeaves(dep.id, ctx) : [dep.id];
        for (const t of targets) {
          if (!isEligibleItem(t, ctx)) continue;
          if (!have(t)) extra.add(t);
          addDeps(t, depth + 1, seen);
        }
      }
    }
  };

  for (const id of picked) {
    const m = ctx.byId.get(id);
    if (!m || !isEligibleItem(id, ctx)) continue;
    addDeps(id, 0, new Set());
    if (isGroupNode(m) || isMemberNode(m)) continue;
    // iki seviyeli aile (taslak `familyBelow` ile aynı yürüyüş): grup her zaman, üye uygunsa
    for (const f of familyBelow(id, ctx)) {
      if (have(f)) continue;
      if (isGroupNode(ctx.byId.get(f)) || isEligibleItem(f, ctx)) extra.add(f);
    }
  }
  return extra.size;
}

/** "Pakettekileri gizle": pakette olan satırlar ve içi boşalan kategoriler listeden düşer. */
export function hideInPackage<T extends DraftMetric>(
  metrics: readonly T[],
  members: ReadonlySet<string>,
): T[] {
  const byId = new Map(metrics.map((m) => [m.id, m]));
  const hasKid = new Set(metrics.map((m) => m.parentId).filter((x): x is string => x !== null));
  const keep = new Set<string>();
  for (const m of metrics) {
    if (hasKid.has(m.id) || members.has(m.id)) continue; // yaprak adayı
    for (
      let cur: T | undefined = m;
      cur && !keep.has(cur.id);
      cur = cur.parentId ? byId.get(cur.parentId) : undefined
    ) {
      keep.add(cur.id);
    }
  }
  return metrics.filter((m) => keep.has(m.id));
}
